import React from 'react'
import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Switch, Typography } from '@mui/material'
import { type FormikContextType, useFormikContext } from 'formik'
import { type SolarProjectUpdateDto } from '../../../../api-schema-types/solar/SolarProjectDomainDtos'
import FormikDateField from '../../../components/ui-widgets/FormikDateField'
import { FormikFieldErrors } from '../../../components/ui-widgets/FormikFieldErrors'
import FormikTextField from '../../../components/ui-widgets/FormikTextField'
import { type EditSolarPlantComponentProps } from './EditSolarPlantForm'

const EditIdentifierSummary: React.FC<EditSolarPlantComponentProps> = ({ solarProject }: EditSolarPlantComponentProps) => {
  const formikProps: FormikContextType<SolarProjectUpdateDto> = useFormikContext()
  const formikErrors = new FormikFieldErrors(formikProps)

  const handleGridOutageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.checked) {
      formikProps.setFieldValue('summary.gridOutageTrackingEnabled', false)
    } else if (event.target.checked) {
      formikProps.setFieldValue('summary.gridOutageTrackingEnabled', true)
    }
  }

  const handlePublishedChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.checked) {
      formikProps.setFieldValue('summary.published', false)
    } else if (event.target.checked) {
      formikProps.setFieldValue('summary.published', true)
    }
  }

  return (
    <>
      <Box sx={{ pb: 4 }}>
        <h3>Identifier</h3>

        <Grid container spacing={4} >

          {/* ==================================================================================================== */}

          <Grid item md={2}>
            <FormikTextField formikProps={formikProps} fieldName={'identifier.projectId'} label={'Proxima Project ID'} type={'text'} locked={true}/>
          </Grid>

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'identifier.projectName'} label={'Project Name'} type={'text'} locked={true}/>
          </Grid>

          <Grid item md={2}>
            <FormikTextField formikProps={formikProps} fieldName={'identifier.solarProjectId'} label={'Solar Project ID'} type={'text'} locked={true}/>
          </Grid>

          <Grid item md={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>Project Status</InputLabel>
              <Select
                {...formikProps.getFieldProps('summary.projectStatus')}
                label={'Project Billing Type'}
                disabled={false}
                error={formikErrors.fieldInError('summary.projectStatus')}
              >
                {
                  solarProject.lookups.projectStatusTypes.map((c) =>
                    <MenuItem key={c.code} value={c.code}>{c.description}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormHelperText>{formikErrors.getError('summary.projectStatus')}</FormHelperText>
          </Grid>

          <Grid item md={2} >
            <Typography variant={'body2'}>Published</Typography>
            <Switch
              checked={formikProps.values.summary.published}
              disabled={false}
              onChange={handlePublishedChange}
              color="primary"
            />
          </Grid>

            {/* ==================================================================================================== */}

        </Grid>

        <h3>Size</h3>

        <Grid container spacing={4} >

          {/* ==================================================================================================== */}

          <Grid item md={2}>
            <FormikTextField formikProps={formikProps} fieldName={'summary.kwpOutput'} label={'kWp'} type={'text'} locked={true} adornment={'kWp'}/>
          </Grid>

          <Grid item md={1}>
            <Typography variant={'body2'}>Battery Included</Typography>
            <Switch
              checked={formikProps.values.summary.includesBattery}
              disabled={true}
              color="primary"
            />
          </Grid>

          { formikProps.values.summary.includesBattery
            ? (
            <>
              <Grid item md={2}>
                <FormikTextField formikProps={formikProps} fieldName={'summary.batteryNameplateCapacityKwh'} label={'Battery Nameplate'} type={'text'} locked={true} adornment={'kWh'}/>
              </Grid>

              <Grid item md={2}>
                <FormikTextField formikProps={formikProps} fieldName={'summary.batteryDischargeCapacityKwh'} label={'Battery Discharge'} type={'text'} locked={true} adornment={'kWh'}/>
              </Grid>

              <Grid item md={5}></Grid>
            </>
              )
            : (
            <Grid item md={9}></Grid>
              )}

          {/* ==================================================================================================== */}

        </Grid>

        <h3>Dates</h3>

        <Grid container spacing={4} >

          {/* ==================================================================================================== */}

          <Grid item md={3}>
            <FormikDateField fieldName={'lifecycleDates.earlyEnergyDate'}
                             label={'Early Energy Date'}
                             locked={false} />
          </Grid>

          <Grid item md={3}>
            <FormikDateField fieldName={'lifecycleDates.assetLeaseCommercialOperationDate'}
                             label={'Asset Lease Commercial Operation Date'}
                             locked={false} />
          </Grid>

          <Grid item md={3}>
            <FormikDateField fieldName={'lifecycleDates.supplierCommercialOperationDate'}
                             label={'Supplier Commercial Operation Date'}
                             locked={false} />
          </Grid>
          {/* ==================================================================================================== */}

        </Grid>

        <h3>Grid outages, forecasting</h3>

        <Grid container spacing={4} >

          {/* ==================================================================================================== */}

          <Grid item md={2}>
            <Typography variant={'body2'}>Grid Outage Tracking Enabled</Typography>
            <Switch
              checked={formikProps.values.summary.gridOutageTrackingEnabled}
              disabled={false}
              onChange={handleGridOutageChange}
              color="primary"
            />
          </Grid>

        </Grid>

      </Box>
    </>
  )
}

export default EditIdentifierSummary
